import { merge } from 'lodash';
import { ReviewContext, ReviewMachine } from '~/machines/review-form';
import { ContextSendMatch } from '~/machines/utils/types';
import { LDClient, LDFlags } from '~/providers';
import { DeepPartial } from '~/typings/utils';
import { defaultCopy, ReviewForm, ReviewFormProps } from './review-form.component';

type VnmTermsContainerProps = {
  micro: ContextSendMatch<ReviewMachine>;
  overrideCopy?: ReviewFormProps['overrideCopy'];
  overrideProps?: DeepPartial<ReviewFormProps>;
};

const copyInitiator = (overrideCopy, context: ReviewContext) => {
  let overrideCopyCallback;

  switch (true) {
    case LDClient.variation(LDFlags.enableAttestationFields):
      overrideCopyCallback = () => {
        const copyFromProps = overrideCopy ? context : {};
        return {
          ...copyFromProps,
          descriptionLineOne: defaultCopy().descriptionLineOneNJSelfAttestation,
          descriptionLineTwo: defaultCopy().descriptionLineTwoNJSelfAttestation,
        };
      };
      break;
    case LDClient.variation(LDFlags.ffUseGeoCoderForMd):
      overrideCopyCallback = () => {
        const copyFromProps = overrideCopy ? context : {};
        return {
          ...copyFromProps,
          descriptionLineOne: defaultCopy().descriptionLineOneMDSelfAttestation,
          descriptionLineTwo: '',
          next: defaultCopy().nonLmiNext,
        };
      };
      break;
    default:
      overrideCopyCallback = () => {
        const copyFromProps = overrideCopy ? context : {};
        return {
          ...copyFromProps,
          descriptionLineOne: defaultCopy().defaultDescriptionLineOne,
          descriptionLineTwo: defaultCopy().defaultDescriptionLineTwo,
        };
      };
      break;
  }
  return overrideCopyCallback;
};

export const ReviewFormContainer = ({
  micro: { context, matches, send },
  overrideCopy,
  overrideProps,
}: VnmTermsContainerProps) => {
  const props: ReviewFormProps = {
    context: {
      billingSelection: context.billingSelection,
      funnelPacket: context.funnelPacket,
      contracts: context.vnmSolarProjectContracts,
      email: context.funnelPacket.user.email,
      firstName: context.funnelPacket.user.firstName,
      lastName: context.funnelPacket.user.lastName,
      lmiStatus: context.lmiStatus!,
      paymentMethod: context.paymentMethod,
      phoneNumber: context.funnelPacket.user.phoneNumber,
      promoCode: context.funnelPacket.user.promoCode,
      solarSavingsRate: context.solarSavingsRate,
      utility: context.funnelPacket.utility,
      utilityAccount: context.utilityAccount,
    },
    loading: matches('review.fetchingUtilityAccount'),
    next: data => send({ type: 'SUBMIT_TERMS', data }),
    flags: {
      showSelfAttestationForm: !!LDClient.variation(LDFlags.enableAttestationFields),
      showLmiOptions:
        !!LDClient.variation(LDFlags.ffUseGeoCoderForMd) ||
        LDClient.variation(LDFlags.enableLmiOptionsRadioButtons),
      requiresAddressFields: context.requiresAddressFields,
    },
    overrideCopy: copyInitiator(overrideCopy, context),
  };

  return <ReviewForm {...merge(props, overrideProps)} />;
};
